import type { Span } from './types';

// ---------------------------------------------------------------------------
// Fold a streamed thought back into one row.
//
// Before the backend coalesced them (#7505), a replay persisted every streamed delta of a model
// turn as its own reasoning row — each a longer prefix of the same text — so a step that had ~8
// thoughts rendered 44 rows, some with a dozen near-copies of one sentence. This is the display-
// side repair for those stored runs; the data is untouched.
// ---------------------------------------------------------------------------

function thoughtText(span: Span): string {
  return span.reasoning?.trim() ?? '';
}

/**
 * Drop every reasoning row whose text is a prefix of a LATER reasoning row, keeping the longest.
 *
 * - Merges across the actions in between: the old rows interleave a thought's deltas with the
 *   clicks it led to, and a resumed thought restates its opening.
 * - The survivor takes the place and start time of the FIRST row in its chain — when the thought
 *   began — and records the ids it absorbed in `mergedIds`, so a selection that pointed at one of
 *   them still resolves to it.
 * - Rows whose texts merely differ are never merged, and non-reasoning rows keep their positions.
 *
 * Pure: returns a new array and new survivor objects; the input is not mutated.
 */
export function mergeStreamedReasoning(
  spans: Span[],
  isReasoning: (span: Span) => boolean,
): Span[] {
  // Each index's final home: the survivor it merged into (by index), or itself.
  const survivorOf = new Map<number, number>();
  // The index of the earliest row in each survivor's chain.
  const chainStart = new Map<number, number>();
  const survivors: number[] = [];

  for (let i = spans.length - 1; i >= 0; i -= 1) {
    const span = spans[i];
    const text = thoughtText(span);
    if (!isReasoning(span) || !text) continue;
    // Nearest later survivor that continues this text (itself included, for an exact duplicate).
    const into = survivors.find((s) => thoughtText(spans[s]).startsWith(text));
    if (into === undefined) {
      survivors.unshift(i);
      chainStart.set(i, i);
    } else {
      survivorOf.set(i, into);
      chainStart.set(into, i);
    }
  }
  if (survivorOf.size === 0) return spans;

  const absorbed = new Map<number, string[]>();
  for (const [dropped, into] of survivorOf) {
    absorbed.set(into, [...(absorbed.get(into) ?? []), spans[dropped].id]);
  }
  // Survivors re-seated at the start of their chain.
  const seatedAt = new Map<number, Span>();
  for (const s of survivors) {
    const start = chainStart.get(s) ?? s;
    const merged = absorbed.get(s);
    seatedAt.set(
      start,
      merged
        ? {
            ...spans[s],
            startMs: spans[start].startMs,
            mergedIds: new Set([...(spans[s].mergedIds ?? []), ...merged]),
          }
        : spans[s],
    );
  }

  // Every index that is a reasoning row folded somewhere: merged away, or a survivor that may have
  // moved to the start of its chain. Only rows outside both keep their own place.
  const folded = new Set<number>([...survivorOf.keys(), ...survivors]);
  const out: Span[] = [];
  spans.forEach((span, i) => {
    const seated = seatedAt.get(i);
    if (seated) out.push(seated);
    else if (!folded.has(i)) out.push(span);
  });
  return out;
}
